import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { PriorityPill } from "@/components/app/ScoreBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { InfoTip } from "@/components/app/InfoTip";

const TYPE_TERMS = { "Title": "TITLE", "Bullet Points": "BULLETS", "Backend Keywords": "BACKEND_KW" };

const TYPES = ["Title", "Bullet Points", "Backend Keywords"];
const STATUSES = ["Open", "In Progress", "Completed", "Rejected", "On Hold"];

export default function ListingOptimization() {
  const { selectedBrandId } = useBrands();
  const [items, setItems] = useState([]);

  const load = () => {
    if (!selectedBrandId) return;
    api.get(`/listing-suggestions?brand_id=${selectedBrandId}`).then((r) => setItems(r.data.items || []));
  };
  useEffect(load, [selectedBrandId]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/listing-suggestions/${id}`, { status });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) { toast.error(extractError(e)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* ignore */ }
  };

  const grouped = items.reduce((acc, it) => {
    if (!acc[it.suggestion_type]) acc[it.suggestion_type] = [];
    acc[it.suggestion_type].push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Execution</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Listing Optimization</h1>
        <p className="mt-1 text-sm text-slate-500">{items.length} suggestions · side-by-side current vs recommended content per SKU.</p>
      </div>

      <Tabs defaultValue="Title">
        <TabsList>
          {TYPES.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`listing-tab-${t}`}>
              <span className="inline-flex items-center gap-1">
                {t} <InfoTip term={TYPE_TERMS[t]} />
              </span>
              <span className="ml-1.5 text-[10px] text-slate-500">({(grouped[t] || []).length})</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {TYPES.map((t) => (
          <TabsContent key={t} value={t}>
            <div className="space-y-4" data-testid={`listing-list-${t}`}>
              {(grouped[t] || []).map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900 tracking-tight">{s.product_name}</h3>
                      <div className="mt-1 flex items-center gap-2">
                        <PriorityPill priority={s.priority} />
                        <span className="text-[11px] uppercase tracking-widest text-slate-500">{s.suggestion_type}</span>
                      </div>
                    </div>
                    <select value={s.status} onChange={(e) => updateStatus(s.id, e.target.value)} data-testid={`listing-status-${s.id}`} className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1">
                      {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Block label="Current" tone="muted" content={s.current_content} />
                    <Block
                      label="Recommended"
                      tone="emerald"
                      content={s.suggested_content}
                      action={
                        <Button size="sm" variant="ghost" onClick={() => copy(s.suggested_content)} className="gap-1 text-xs h-7">
                          <Copy className="h-3 w-3" /> Copy
                        </Button>
                      }
                    />
                  </div>
                  <div className="mt-3 text-xs text-slate-500">{s.reason}</div>
                  {(s.keywords_used || []).length ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {s.keywords_used.map((k, i) => (
                        <span key={i} className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                          <Check className="h-3 w-3 mr-0.5" /> {k}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
              {!(grouped[t] || []).length && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
                  No {t.toLowerCase()} suggestions for this week.
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function Block({ label, content, tone, action }) {
  const tones = {
    muted: "border-slate-200 bg-slate-50",
    emerald: "border-emerald-200 bg-emerald-50/50",
  };
  return (
    <div className={`rounded-lg border ${tones[tone]} p-4 relative`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        {action}
      </div>
      <pre className="whitespace-pre-wrap break-words text-sm text-slate-800 font-sans leading-relaxed min-h-[60px]">
        {content || <span className="text-slate-400 italic">— empty —</span>}
      </pre>
    </div>
  );
}
